import { AufnahmePunktDocument, isM } from 'src/app/data-model';

/**
 * Vorschlag fuer die Substratgruppe der Stamm-Standortformengruppe.
 *
 * Die Substratgruppe (S, bS, lS, G, bG, F, bF, R, K, dK, tK, L, dL, tL, T, dT,
 * tT, H) steht im Bodenformenkatalog an der HAUPTbodenform und gilt damit fuer
 * alle Feinbodenformen desselben Steckbriefs. Wer eine Feinbodenform waehlt, hat
 * die Substratgruppe damit faktisch schon gewaehlt - sie noch einmal von Hand
 * einzutippen ist doppelte Arbeit und eine Fehlerquelle.
 *
 * Der Weg der Angabe:
 *   Katalog (Bodensteckbriefe, `hauptbodenform.substratgruppe`)
 *     -> Feinbodenformen-Liste (abgeleitetes Feld `STG`, feinbodenform-derivation.ts)
 *     -> dieses Lookup (vom DataService beim Hydrieren des Listen-Stores gefuellt)
 *     -> `extendedHeader.stfg.substratGruppe`
 *
 * ## Ueberschreibbar
 *
 * Gleiche Bauart wie der Entwaesserungs-Vorschlag (`evaluators/entwaesserung.ts`):
 * Der zuletzt vorgeschlagene Wert wird am Punkt gemerkt. Stimmt das Feld mit ihm
 * ueberein, stammt der Wert aus dem Vorschlag und wird bei einem Wechsel der
 * Feinbodenform nachgezogen. Weicht es ab - oder fehlt der gemerkte Wert, wie bei
 * Altdaten und Importen -, gilt es als von Hand gesetzt und bleibt unangetastet.
 *
 * ## Wann NICHT vorgeschlagen wird
 *
 * - **Moorpunkte** (Typ 7): Dort wird die Substratgruppe nicht separat angegeben
 *   (dieselbe Regel wie in der Sammelbearbeitung, `mineralOnlyPathAccessor`).
 * - **Uneindeutige Bodenform**: Bei einer zusammengesetzten Bodenform ("x / y")
 *   koennen die Glieder in verschiedenen Substratgruppen liegen. Es gibt aber nur
 *   EIN Feld. Nennen alle gefuellten Glieder dieselbe Gruppe, ist die Sache klar;
 *   widersprechen sie sich, wird nichts vorgeschlagen - raten waere schlimmer als
 *   ein leeres Feld.
 * - **Frei gebildete Feinbodenformen** (Moor-FBF) stehen in keinem Steckbrief und
 *   haben deshalb keine Substratgruppe im Katalog.
 */

/** FBF-Code (ohne Leerzeichen) -> Substratgruppe. */
let STG_ZU_FBF = new Map<string, string>();

/** Schluessel-Form: Leerzeichen raus, damit "Mz LG" und "MzLG" gleich treffen. */
function schluessel(fbf: unknown): string {
	return (fbf == null ? '' : String(fbf)).replace(/\s+/g, '');
}

/**
 * Fuettert das Lookup aus der Feinbodenformen-Liste des zentralen ListStores
 * (`DataService.applyStoreDerived`) - dieselbe Bauart wie `setPunktartList` und
 * `OptionService.setHumusFormList`. Vor dem ersten Aufruf ist das Lookup leer und
 * es wird schlicht nichts vorgeschlagen; die gebuendelte Notliste fuehrt die
 * Substratgruppe naemlich nicht, sie kommt erst aus dem Katalog.
 */
export function setFeinbodenformSubstratgruppen(
	rows: ReadonlyArray<{ FBF?: string; STG?: string }> | undefined | null,
): void {
	const map = new Map<string, string>();
	for (const r of rows ?? []) {
		const code = schluessel(r?.FBF);
		const stg = (r?.STG ?? '').trim();
		// Erste Zeile gewinnt bei (unerwarteten) doppelten Codes - dieselbe Regel
		// wie beim Katalog-Index, der den ersten Treffer als Master behaelt.
		if (code && stg && !map.has(code)) map.set(code, stg);
	}
	STG_ZU_FBF = map;
}

/** Substratgruppe zu einer Feinbodenform; undefined, wenn unbekannt. */
export function substratgruppeZuFbf(fbf: string | undefined | null): string | undefined {
	const code = schluessel(fbf);
	return code ? STG_ZU_FBF.get(code) : undefined;
}

/**
 * Substratgruppe, die sich aus der eingetragenen Bodenform ergibt - oder
 * undefined, wenn sie sich nicht eindeutig ergibt (siehe Kopfkommentar).
 */
export function substratgruppeVorschlag(bp: AufnahmePunktDocument | undefined): string | undefined {
	if (!bp || isM(bp)) return;
	const glieder = bp.header?.bodenform?.bf ?? [];
	let gefunden: string | undefined;
	for (const glied of glieder) {
		if (!glied?.fbf?.trim()) continue; // leeres Glied sagt nichts
		const stg = substratgruppeZuFbf(glied.fbf);
		if (!stg) return; // ein unbekanntes Glied macht die Bodenform unklar
		if (gefunden && gefunden !== stg) return; // Glieder widersprechen sich
		gefunden = stg;
	}
	return gefunden;
}

/**
 * Traegt den Vorschlag ein und zieht ihn bei einem Wechsel der Feinbodenform
 * nach - aber nur, solange der Wert vom Vorschlag stammt.
 */
export function applySubstratgruppeVorschlag(bp: AufnahmePunktDocument | undefined): void {
	const stfg = bp?.extendedHeader?.stfg;
	if (!stfg) return;
	const wert = stfg.substratGruppe;
	const gemerkt = stfg.substratGruppeVorschlag;
	// Von Hand gesetzt (oder Altbestand/Import) -> eingefroren.
	if (wert && wert !== gemerkt) return;

	const vorschlag = substratgruppeVorschlag(bp);
	if (!vorschlag) return;
	stfg.substratGruppe = vorschlag;
	stfg.substratGruppeVorschlag = vorschlag;
}
